import _ from 'lodash';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

import { CommonIcons } from 'common-src/assets/Icons';
import { DatePickerNew, Input, Select } from 'common-src/components/base';
import PatientReferral, {
  catchReferralErrors,
  getInitialReferralData,
  PreferredLocation,
  PreferredLocationOptions,
  ReferralStatusOptions,
  ReferralTypeOptions,
  validatePatientReferral,
} from 'common-src/models/PatientReferral';

import { AddButton, RemoveButton } from 'src/components/buttons';
import { SeparatorLine } from 'src/components/elements';

import styles from './Referrals.module.scss';

const Referrals = ({
  patientId,
  isEditMode,
  formData,
  setFormData,
  errors,
  setErrors,
  patientReferrals,
  addButtonHandler = () => {},
}) => {
  const onChange = (field, value) => {
    const validationField = field.split('.')[1];
    const err = validatePatientReferral(validationField, value);

    setErrors((prev) => ({ ...prev, [field]: err[validationField] }));
    setFormData((prev) => {
      const newFormData = _.cloneDeep(prev);
      _.set(newFormData, field, value);
      return newFormData;
    });
  };

  const onDateChange = (field, date) => {
    const dateToApply = date ? moment(date).format('YYYY-MM-DD') : null;
    onChange(field, dateToApply);
  };

  const renderAddButton = () => {
    if (!isEditMode) return null;

    return (
      <AddButton
        id="add-referral"
        classNames={[styles.button]}
        label="Add referral"
        onClick={() => {
          setFormData((prev) => [...prev, getInitialReferralData(patientId)]);
          addButtonHandler();
        }}
      />
    );
  };

  const renderRemoveButton = (referral) => {
    if (!isEditMode) return <div />;

    return (
      <RemoveButton
        id="remove-referral"
        onClick={() => {
          const { errors } = catchReferralErrors(_.without(formData, referral));
          setErrors(errors);
          setFormData((prev) => _.without(prev, referral));
        }}
      />
    );
  };

  const renderCreatedBy = (referralId, index) => {
    if (isEditMode) return null;

    const referral = patientReferrals.find((referral) => referral.id === referralId);

    return (
      <Input
        id={`referral-created-by-${index}`}
        label="Created By"
        readOnly
        value={referral.createdBy?.getName()}
        size="small"
      />
    );
  };

  return (
    <div>
      {formData.map((referral, index) => (
        <div key={`referral-${index}`}>
          <div className={styles.grid}>
            <Select
              containerId={`${index}.referralType`}
              id={`referral-type-${index}`}
              placeholder="Select referral type"
              options={ReferralTypeOptions}
              value={ReferralTypeOptions.find((op) => op.value === referral.referralType)}
              label="Referral Type"
              required
              readOnly={!isEditMode}
              onChange={(option) => {
                onChange(`${index}.referralType`, option.value);
              }}
              position="absolute"
              paperHeight={250}
              size="small"
              errorText={errors[`${index}.referralType`]}
              isSearchable
            />
            <Select
              containerId={`${index}.status`}
              id={`referral-status-${index}`}
              placeholder="Select status"
              options={ReferralStatusOptions}
              value={ReferralStatusOptions.find((op) => op.value === referral.status)}
              label="Status"
              required
              readOnly={!isEditMode}
              onChange={(option) => {
                onChange(`${index}.status`, option.value);
              }}
              position="absolute"
              paperHeight={250}
              size="small"
              errorText={errors[`${index}.status`]}
            />
            <DatePickerNew
              containerId={`${index}.dateSent`}
              id={`referral-date-sent-${index}`}
              header="Date Sent"
              placeholder="MM/YYYY"
              maxDate={new Date()}
              value={referral.dateSent}
              iconSrc={CommonIcons.calendarCheckBoldIcon}
              onDateSelected={(date) => onDateChange(`${index}.dateSent`, date)}
              onBlur={(date) => onDateChange(`${index}.dateSent`, date)}
              errorText={errors[`${index}.dateSent`]}
              size="small"
              readOnly={!isEditMode}
              required
            />
            <Select
              containerId={`${index}.preferredLocation`}
              id={`preferred-location-${index}`}
              placeholder="Select preferred location"
              options={PreferredLocationOptions}
              value={PreferredLocationOptions.find((op) => op.value === referral.preferredLocation)}
              label="Preferred Location"
              readOnly={!isEditMode}
              onChange={(option) => {
                onChange(`${index}.preferredLocation`, option.value);

                if (option.value !== PreferredLocation.Other) {
                  onChange(`${index}.otherLocation`, '');
                }
              }}
              position="absolute"
              paperHeight={250}
              size="small"
            />
            <Input
              containerId={`${index}.otherLocation`}
              id={`other-location-${index}`}
              label="Other Location"
              value={referral.otherLocation}
              size="small"
              readOnly={!isEditMode}
              onFinishEditing={(value) => onChange(`${index}.otherLocation`, value)}
              placeholder="Enter other location"
              disabled={referral.preferredLocation !== PreferredLocation.Other}
              debounced
            />
            {renderCreatedBy(referral.id, index)}
            {renderRemoveButton(referral)}
          </div>
          <SeparatorLine classNames={[styles.separator, 'm-b-20']} />
        </div>
      ))}
      <div className={styles.grid}>{renderAddButton()}</div>
    </div>
  );
};

Referrals.propTypes = {
  patientId: PropTypes.number,
  isEditMode: PropTypes.bool,
  formData: PropTypes.array,
  setFormData: PropTypes.func,
  errors: PropTypes.object,
  setErrors: PropTypes.func,
  patientReferrals: PropTypes.arrayOf(PropTypes.exact(PatientReferral.schema)),
  addButtonHandler: PropTypes.func,
};

export default Referrals;
